import React, { useCallback, useEffect, useState, ReactNode } from 'react';
import { Box, CircularProgress, Typography, SvgIcon } from '@mui/material';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import useScreenSize from '../../utils/useScreenSize';
import PublicContentLayout from './PublicContentLayout';

export interface PartWithId {
  id: string;
  type: string;
  [key: string]: unknown;
}

export type PartWithIdLike = {
  id: string;
  type: string;
};

export interface PublicContentData<T extends PartWithId | PartWithIdLike> {
  id: string;
  title?: string;
  items: T[];
  parts?: T[]; // Optional for backward compatibility with PublicLernset type
}
export interface PublicContentViewProps<T extends PartWithId | PartWithIdLike> {
  accessToken: string;
  fetchData: (accessToken: string) => Promise<PublicContentData<T>>;
  fetchItemImages?: (data: PublicContentData<T>, accessToken: string) => Promise<{ [key: string]: string }>;
  renderItem: (item: T, imageUrlById?: { [key: string]: string }) => ReactNode;
  onDownload?: (data: PublicContentData<T>, accessToken: string) => Promise<void>;
  onPrint?: (data: PublicContentData<T>, accessToken: string) => Promise<void>;
  emptyStateIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  defaultImageWidth?: number;
  defaultImageHeight?: number;
  onError?: (error: unknown) => void;
  renderTopBarButtons?: (props: {
    data: PublicContentData<T> | undefined;
    isEmpty: boolean;
    onDownload: () => Promise<void>;
    onPrint: () => Promise<void>;
    isDownloadDisabled: boolean;
    isPrintDisabled: boolean;
    part?: T;
  }) => ReactNode;
  customLayout?: React.ComponentType<{
    children: ReactNode;
    topRightComponent?: ReactNode;
  }>;
  noHeading?: boolean;
}

const PublicContentView = <T extends PartWithId | PartWithIdLike>({
  accessToken,
  fetchData,
  fetchItemImages,
  renderItem,
  onDownload,
  onPrint,
  emptyStateIcon: EmptyStateIcon,
  emptyStateTitle,
  emptyStateMessage,
  defaultImageWidth,
  onError,
  renderTopBarButtons,
  customLayout,
  noHeading = false,
}: PublicContentViewProps<T>) => {
  const [data, setData] = useState<PublicContentData<T>>();
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);
  const [isPrintDisabled, setIsPrintDisabled] = useState(false);
  const [itemImageUrlById, setItemImageUrlById] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;
  const isEmpty = data ? data.items.length === 0 : true;
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const fetchedData = await fetchData(accessToken);
        setData(fetchedData);

        if (fetchItemImages && fetchedData.items && fetchedData.items.length > 0) {
          const images = await fetchItemImages(fetchedData, accessToken);
          setItemImageUrlById(images);
        }
      } catch (error: unknown) {
        const errorObj = error as Error & { statusCode?: number };
        
        if (onError) {
          onError(error);
        }
        
        // Always show empty state for 403 or expired errors
        // For other errors, show empty state if no onError handler is provided
        if (errorObj.statusCode === 403 || errorObj.message?.includes('expire')) {
          setData({ id: '', title: '', items: [] });
        } else if (!onError) {
          setData({ id: '', title: '', items: [] });
        }
      }
      setIsLoading(false);
    };

    loadData();
  }, [accessToken, fetchData, fetchItemImages, onError]);

  const renderPageCards = useCallback(() => {
    return data?.items.map((item) => renderItem(item, itemImageUrlById));
  }, [data?.items, itemImageUrlById, renderItem]);

  const renderContent = () => {
    if (!isEmpty) {
      return (
        <>
          {!noHeading && <Typography
            variant='h5'
            sx={{
              width: '100%',
              textAlign: 'left',
              ...(defaultImageWidth && { maxWidth: `${defaultImageWidth}px` }),
            }}
          >
            {data?.title}
          </Typography>}
          {renderPageCards()}
        </>
      );
    } else {
      return (
        <Box
          data-testid='empty-state-container'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.md,
            alignItems: 'center',
          }}
        >
          {EmptyStateIcon && (
            <SvgIcon component={EmptyStateIcon} sx={{ width: '100%', height: '100%' }} viewBox='0 0 400 300' />
          )}
          {emptyStateTitle && <Typography variant='h5'>{emptyStateTitle}</Typography>}
          {emptyStateMessage && (
            <Typography variant='body2' sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
              {emptyStateMessage}
            </Typography>
          )}
        </Box>
      );
    }
  };

  const handleDownloadClick = async () => {
    if (!data || !onDownload) return;
    setIsDownloadDisabled(true);
    try {
      await onDownload(data, accessToken);
    } finally {
      setIsDownloadDisabled(false);
    }
  };

  const handlePrintClick = async () => {
    if (!data || !onPrint) return;
    setIsPrintDisabled(true);
    try {
      await onPrint(data, accessToken);
    } finally {
      setIsPrintDisabled(false);
    }
  };

  const circularProgress = () => {
    return (
      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
        <CircularProgress
          color='primary'
          sx={{
            '& .MuiCircularProgress-svg': {
              height: 'auto',
              width: 'auto',
            },
          }}
        />
      </Box>
    );
  };

  const renderLoadingState = () => circularProgress();

  const topRightComponent = renderTopBarButtons
    ? renderTopBarButtons({
      data,
      isEmpty,
      onDownload: handleDownloadClick,
      onPrint: handlePrintClick,
      isDownloadDisabled,
      isPrintDisabled,
      part: data?.items[0],
    })
    : undefined;

  const content = (
    <Box
      sx={{
        padding: isSmallScreen ? undefined : spaces.lg,
        paddingBottom: isSmallScreen ? undefined : spaces.xxxxl,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '100%',
        gap: isXsScreen ? spaces.xs : spaces.md,
        backgroundColor: palette.background.default,
      }}
    >
      {isLoading ? renderLoadingState() : renderContent()}
    </Box>
  );

  if (customLayout) {
    const CustomLayoutComponent = customLayout;
    return (
      <CustomLayoutComponent topRightComponent={topRightComponent}>
        {content}
      </CustomLayoutComponent>
    );
  }

  return (
    <PublicContentLayout topRightComponent={topRightComponent}>
      {content}
    </PublicContentLayout>
  );
};

export default PublicContentView;

